import { StatementFormData } from 'src/Libs/installation-statement/types/general'
import {
  EdisonWallBoxApiDto,
  InstallationStatementAddressDto,
  InstallationStatementApiDto,
  StorageInstallationStatementApiDto,
} from 'src/Libs/installation-statement/models/statement'
import { PhotovoltaicTrackerApiDto } from 'src/Libs/installation-statement/models/photovoltaic'
import { StorageInstallationStatementKeys } from 'src/Libs/installation-statement/models/storage'
import { MeasurementsFormDataKeys } from 'src/Libs/installation-statement/models/measurements'

const convertAddress = (formData: StatementFormData): InstallationStatementAddressDto => {
  const { deviatingAddress, customerAddress, deviatingAddressEnabled, installer, partnerNumber, partnerNumberEnabled, partnerNumberValid } =
    formData.addressData

  const sendPartnerNumber = partnerNumber && partnerNumberEnabled && partnerNumberValid

  return {
    // If deviating address is disabled, installation* fields must be set to the customer* fields.
    // The empty string in the 'truthy' side of the ternary is also crucial, because if deviating address is enabled,
    // but a required field is missing, validation must fail and we mustn't save a customer address field.
    installationCountryCode: deviatingAddressEnabled ? deviatingAddress?.country ?? '' : customerAddress.country,
    installationStreetName: deviatingAddressEnabled ? deviatingAddress?.addressAndHouseNumber ?? null : customerAddress.addressAndHouseNumber,
    installationZip: deviatingAddressEnabled ? deviatingAddress?.zipCode ?? '' : customerAddress.zipCode,
    installationCity: formData.addressData.deviatingAddressEnabled
      ? formData.addressData.deviatingAddress?.city ?? ''
      : formData.addressData.customerAddress.city,

    installerName: installer?.firstName || '',
    installerSurname: installer?.lastName || '',

    customerName: customerAddress.firstName || '',
    customerSurname: customerAddress.lastName || '',
    customerCountryCode: customerAddress.country || '',
    customerStreetName: customerAddress.addressAndHouseNumber || '',
    customerCity: customerAddress.city || '',
    customerZip: customerAddress.zipCode || '',
    ...(sendPartnerNumber ? { partnerNumber: Number(partnerNumber) } : { partnerNumber: null }),
  }
}

const convertStorages = (formData: StatementFormData): StorageInstallationStatementApiDto => {
  const { showEdisonWallboxStep, hasBiDiWallbox, ...rest } = formData.storageData

  const convertToNumberOrNull = (value?: string | null): number | null =>
    value !== null && value !== '' && value !== undefined ? Number(value) : null

  return {
    ...rest,
    ...formData.measurementsData,
    distanceTop: convertToNumberOrNull(rest[StorageInstallationStatementKeys.DistanceTop]),
    distanceFront: convertToNumberOrNull(rest[StorageInstallationStatementKeys.DistanceFront]),
    distanceLeft: convertToNumberOrNull(rest[StorageInstallationStatementKeys.DistanceLeft]),
    distanceRight: convertToNumberOrNull(rest[StorageInstallationStatementKeys.DistanceRight]),
    temperature: convertToNumberOrNull(rest[StorageInstallationStatementKeys.Temperature]),
    loopImpedanceNL1: convertToNumberOrNull(formData.measurementsData[MeasurementsFormDataKeys.LoopImpedanceNL1]),
    loopImpedanceNL2: convertToNumberOrNull(formData.measurementsData[MeasurementsFormDataKeys.LoopImpedanceNL2]),
    loopImpedanceNL3: convertToNumberOrNull(formData.measurementsData[MeasurementsFormDataKeys.LoopImpedanceNL3]),
    loopImpedancePEL: convertToNumberOrNull(formData.measurementsData[MeasurementsFormDataKeys.LoopImpedancePEL]),
  }
}

const convertPvTrackers = (formData: StatementFormData): Array<PhotovoltaicTrackerApiDto> => {
  return formData.photovoltaicData.pvTrackers.map(t => ({
    name: t.name,
    pvStrings: t.pvStrings.map((string, index, allStrings) => {
      const s = t.appliedToAllString ? allStrings[0] : string

      return {
        name: `STRING-${index + 1}`,
        moduleType: s.pvModuleType || '',
        modulePeakPower: Number(s.modulePeakPower),
        openCircuitVoltage: s.openCircuitVoltage,
        riso: s.riso,
        testVoltage: s.testVoltage,
        positiveElectrodeGround: s.positiveElectrodeGround,
        negativeElectrodeGround: s.negativeElectrodeGround,
        powerOptimizer: s.powerOptimizer || 'none',
        pvOrientations: [
          {
            num: s.moduleCount,
            direction: s.direction || null,
            orientation: s.orientation,
          },
        ],
      }
    }),
  }))
}

const convertEdisonWallBox = (formData: StatementFormData): EdisonWallBoxApiDto => {
  const data = formData.edisonData

  if (
    !formData.storageData[StorageInstallationStatementKeys.hasBiDiWallbox] &&
    !formData.storageData[StorageInstallationStatementKeys.showEdisonWallboxStep]
  ) {
    return null
  }

  return {
    edisonConnectSn: data.serialNumbers.connect ?? null,
    edisonPowerSn: data.serialNumbers.power ?? null,
    vehicleBatCapacity: data.vehicleInformation.batteryCapacity ? String(data.vehicleInformation.batteryCapacity) : null,
    vehicleManufacturer: data.vehicleInformation.manufacturer ?? null,
    vehicleModel: data.vehicleInformation.model ?? null,
    vehicleSoftwareVersion: data.vehicleInformation.softwareVersion ?? null,
    edisonCb1: data.functionalityTests.fullyInstalled,
    edisonCb2: data.functionalityTests.configured,
    edisonCb3: data.functionalityTests.scenarioOneTested,
    edisonCb4: data.functionalityTests.scenarioTwoTested,
    edisonCb5: data.functionalityTests.customerBriefed,
    edisonCb6: data.functionalityTests.biDirectionalDCChargingConfiguration.positionedAtChargingPoint,
    edisonCb7: data.functionalityTests.biDirectionalDCChargingConfiguration.chargingLocationDefined,
    edisonCb8: data.functionalityTests.biDirectionalDCChargingConfiguration.functionActivated,
  }
}

export const fromStatementModelToDtoTransformer = (data: StatementFormData): InstallationStatementApiDto => {
  return {
    ...convertAddress(data),
    storage: convertStorages(data),
    pvTrackers: convertPvTrackers(data),
    installationStatementBidi: convertEdisonWallBox(data),
  }
}
